/** Everything the app keeps on the device: the group IDs, and who you said you are (§5). */
import { normalizeGroupId } from '@topspin/shared';

const KEYS = 'topspin.keys';
const PLAYER = 'topspin.playerId';
const CALENDAR_URL = 'topspin.calendarUrl';

function readJson<T>(name: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(name);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function getKeys(): string[] {
  const value = readJson<unknown>(KEYS, []);
  if (!Array.isArray(value)) return [];
  // Filtered through normalize, so a key stored by an older build can't reach the API.
  return value.flatMap((v) => {
    const groupId = typeof v === 'string' ? normalizeGroupId(v) : undefined;
    return groupId ? [groupId] : [];
  });
}

/** Takes the canonical form only — callers verify with the API before getting here (§5). */
export function addKey(key: string): string[] {
  const groupId = normalizeGroupId(key);
  const keys = getKeys();
  if (!groupId) return keys;
  // Jeff enters both group IDs; the app accumulates them so both nights show on one screen (§5).
  if (!keys.includes(groupId)) keys.push(groupId);
  localStorage.setItem(KEYS, JSON.stringify(keys));
  return keys;
}

export function removeKey(key: string): string[] {
  const keys = getKeys().filter((k) => k !== key);
  localStorage.setItem(KEYS, JSON.stringify(keys));
  return keys;
}

export function clearKeys(): void {
  localStorage.removeItem(KEYS);
}

export function getPlayerId(): string | null {
  return localStorage.getItem(PLAYER);
}

export function setPlayerId(playerId: string): void {
  localStorage.setItem(PLAYER, playerId);
}

export function clearPlayerId(): void {
  localStorage.removeItem(PLAYER);
}

/**
 * The calendar link this device minted. Kept here because the server stores only its hash and
 * cannot show it again — the same trade `mint-token` makes. A device that has lost it mints a
 * fresh one, which revokes the old.
 *
 * Whether a subscription *exists* is not read from here: that comes off the server, so a device
 * that didn't mint it still tells the truth about it.
 */
export function getCalendarUrl(): string | null {
  return localStorage.getItem(CALENDAR_URL);
}

export function setCalendarUrl(url: string): void {
  localStorage.setItem(CALENDAR_URL, url);
}

export function clearCalendarUrl(): void {
  localStorage.removeItem(CALENDAR_URL);
}

/**
 * Reads `?k=…` and strips it from the URL. Group IDs are typed in now, precisely because an
 * iOS home-screen icon opens in its own storage sandbox where a URL-captured one is lost
 * (§5) — but the dev server still prints these links, and old ones are still in people's
 * text history, so honour them and strip them either way.
 */
export function captureKeyFromUrl(): string | null {
  const url = new URL(window.location.href);
  const key = url.searchParams.get('k');
  if (!key) return null;
  const groupId = normalizeGroupId(key);
  if (groupId) addKey(groupId);
  url.searchParams.delete('k');
  window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  return groupId ?? null;
}
